import type { ReactNode } from 'react';

import { PLURAL_PLACEHOLDER_NAME, containsWrapper } from './constants';
import { interpolatePlaceholders } from './interpolatePlaceholders';
import { interpolateWithWrappers } from './interpolateWithWrappers';
import { lookup } from './lookup';
import type {
  ElementOrStringPlaceholderAndWrapperMap,
  ElementOrStringPlaceholderMap,
  PhraseMap,
  StringPlaceholderAndWrapperMap,
  WrapperMap,
} from './types';

const normalizeEmptyWrappers = (str: string, placeholderNames: string[]): string =>
  // split().join() is equivalent to replaceAll(), which requires ES2021 lib
  placeholderNames.reduce((result, name) => result.split(`[${name}][/]`).join(`{${name}}`), str);

/**
 * Unified template interpolation: resolves wrappers first (structural parsing on the raw template),
 * then resolves placeholders in each resulting text segment. This ordering prevents user-provided
 * placeholder values from being reinterpreted as wrapper syntax.
 */
export function interpolateTemplate(
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholdersAndWrappers?: StringPlaceholderAndWrapperMap
): string;
export function interpolateTemplate(
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholdersAndWrappers: ElementOrStringPlaceholderAndWrapperMap
): ReactNode;
export function interpolateTemplate(
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholdersAndWrappers: ElementOrStringPlaceholderAndWrapperMap = {}
): string | ReactNode[] {
  const placeholders: ElementOrStringPlaceholderMap = {};
  const wrapperFunctions: WrapperMap = {};
  for (const [name, value] of Object.entries(placeholdersAndWrappers)) {
    if (typeof value === 'function') {
      wrapperFunctions[name] = value;
    } else {
      placeholders[name] = value;
    }
  }

  const rawStr = lookup(locale, phrases, key, placeholders[PLURAL_PLACEHOLDER_NAME]);
  if (!rawStr) {
    return '';
  }

  const str = normalizeEmptyWrappers(rawStr, Object.keys(placeholders));

  if (!containsWrapper(str)) {
    return interpolatePlaceholders(str, locale, placeholders);
  }

  return interpolateWithWrappers(str, locale, placeholders, wrapperFunctions);
}

export const getStringInterpolator = (
  feature: string
): ((
  locale: string,
  phrases: PhraseMap
) => Record<string, (placeholdersAndWrappers: ElementOrStringPlaceholderAndWrapperMap) => ReactNode | string>) & {
  readonly feature: string;
} => {
  const StringInterpolator = function (locale: string, phrases: PhraseMap) {
    const placeholderObj = {};

    return new Proxy(placeholderObj, {
      get(_target, prop: string) {
        return function (placeholdersAndWrappers: ElementOrStringPlaceholderAndWrapperMap = {}) {
          return interpolateTemplate(locale, phrases, prop, placeholdersAndWrappers);
        };
      },
    });
  };
  StringInterpolator.feature = feature;

  return StringInterpolator as ((
    locale: string,
    phrases: PhraseMap
  ) => Record<string, (placeholdersAndWrappers: ElementOrStringPlaceholderAndWrapperMap) => ReactNode | string>) & {
    readonly feature: string;
  };
};
